import List from '../../../generic/List';
import type { Repository } from '../../../../types';

import ConnectableProjectRow from './ConnectableProjectRow';
import DisconnectedProjectRow from './DisconnectedProjectRow';
import ImportedProjectRow from './ImportedProjectRow';
import UnimportedProjectRow from './UnimportedProjectRow';
import type { OnBranchChangeFn, OnImportRepositoryFn, OnSelectRepositoryFn } from './types';

import styles from './index.module.less';

export type HybridProjectsServiceImportRepositoriesTableProps = {
  repositories: Repository[];
  onBranchChange: OnBranchChangeFn;
  selectedRepos: Repository[];
  onSelectRepository: OnSelectRepositoryFn;
  onImportRepository: OnImportRepositoryFn;
};

/**
 * A table component just for hybrid-project imports
 *
 * repositories - Row data for the table
 */
const HybridProjectsTable = ({
  repositories,
  onBranchChange,
  selectedRepos,
  onSelectRepository,
  onImportRepository,
} : HybridProjectsServiceImportRepositoriesTableProps) => {
  return (
    <div className={styles.hybridTable}>
      <List>
        <List.Header>
          <span className={styles.column} />
          <span className={styles.column}>Title</span>
          <span className={styles.column}>Branch</span>
          <span className={styles.column}>Status</span>
          <span className={`${styles.column} ${styles.actionColumn}`}>Action</span>
        </List.Header>
        <List.Body>
          {
            repositories.map((repo: Repository) => {
              const isConnected = !!repo.connectedProjects?.length;
              const isConnectable = !!repo.connectableProjects?.length;

              // Quick Import projects will have their "repo.project" field populated.
              // CLI Upload (hybrid) projects will not so we must instead check connection status.
              const isImported = !!repo.project || isConnected;

              // Both Quick Import (autobuild) and CLI Upload (managed, when hybrid) projects can become disconnected.
              const isUtilizingGitHubApp = repo.project?.vcs_host === 'github-app';

              // Normally, repo.connectedProjects and repo.connectableProjects are mutually exclusive. However,
              // prior to existence of "hybrid projects", users could import the same repo via both QI and CLI.
              // These projects will show up as both connected (because their QI project is connected) and connectable
              // (because their CLI project is not). So, we need to specifically check for being connectable yet NOT connected.
              const isDisconnected = isConnectable && !isConnected && isUtilizingGitHubApp;
              if (isDisconnected) {
                return (
                  <DisconnectedProjectRow
                    repo={repo}
                    selectedRepos={selectedRepos}
                    onBranchChange={onBranchChange}
                    onSelectRepository={onSelectRepository}
                    onImportRepository={onImportRepository}
                  />
                );
              }

              if (isImported) {
                return (
                  <ImportedProjectRow repo={repo} />
                );
              }

              if (isConnectable) {
                return (
                  <ConnectableProjectRow
                    repo={repo}
                    selectedRepos={selectedRepos}
                    onBranchChange={onBranchChange}
                    onSelectRepository={onSelectRepository}
                    onImportRepository={onImportRepository}
                  />
                );
              }

              return (
                <UnimportedProjectRow
                  repo={repo}
                  onBranchChange={onBranchChange}
                  onSelectRepository={onSelectRepository}
                  selectedRepos={selectedRepos}
                  onImportRepository={onImportRepository}
                />
              );
            })
          }
        </List.Body>
      </List>
    </div>
  );
};

export default HybridProjectsTable;
